import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type TenantContribution = {
  propertyid: number
  tenantcontributionid: number
}
// private module logic //

const id: string = "tenantContributions"
const initialState: ApiResult<TenantContribution[]> = { id, state: "init", value: [] }
const tenantContributionsStream = new BehaviorSubject<ApiResult<TenantContribution[]>>(initialState)
const tenantContributionsEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let tenantContributionsMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function sortTenantContributions(data: TenantContribution[]): TenantContribution[] {
  const type_order = ["building_capital", "expense", "misc_income"]

  data.sort((a, b) => {
    let A = a["type"], Ap = a["code"], B = b["type"], Bp = b["code"]

    if (type_order.indexOf(A) < type_order.indexOf(B)) return 1
    else if (type_order.indexOf(A) > type_order.indexOf(B)) return -1

    if(Ap > Bp) return 1
    else if(Ap < Bp) return -1
    else return 0
  });

  return data
}

function clear(): void {
  tenantContributionsStream.next({ id, state: 'ok', value: [] })
  tenantContributionsMap = {}
}

function get(tenantcontributionid: number): TenantContribution {
  return tenantContributionsMap[tenantcontributionid]
}

function refresh(): Promise<any> {
  const currentValue = tenantContributionsStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchTenantContributions(propertyid)
}

function fetchTenantContributions(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<TenantContribution[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  tenantContributionsStream.next({ id, state: 'busy', value: tenantContributionsStream.getValue().value })

  let url = `tenantcontribution?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `tenantcontribution?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: TenantContribution[]) => {
    _.forEach(records, (tenantContributions) => (tenantContributionsMap[tenantContributions.tenantcontributionid] = tenantContributions))
    // merge results unique by tenantcontributionid
    tenantContributionsStream.next({ id, state: 'ok', value: sortTenantContributions(records) })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number): Promise<TenantContribution | { message: string }> {
  const currentValue = tenantContributionsStream.getValue().value
  tenantContributionsStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    tenantgroupdetail: {
      items: [],
      reportTagNamedItemId: ""
    }
  }

  return Client.post(`tenantcontribution`, payload)
    .then((tenantContributions: TenantContribution) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, tenantContributions.tenantcontributionid),
        redofunc: () => create(propertyid)
      })
      tenantContributionsMap[tenantContributions.tenantcontributionid] = tenantContributions
      // merge results unique by tenantcontributionid
      const currentValue = tenantContributionsStream.getValue().value
      tenantContributionsStream.next({
        id,
        state: 'ok',
        value: _.unionBy([tenantContributions], currentValue, 'tenantcontributionid')
      })
      tenantContributionsEventStream.next({ type: 'TENANTCONTRIBUTION_CREATED', payload: tenantContributions })
      return tenantContributions
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      tenantContributionsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchTenantContributions(propertyid)
      return err
    })
}

function copy(propertyid:number, tenantcontributionid:number, code:string) {
  const currentValue = tenantContributionsStream.getValue().value;
  tenantContributionsStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'tenantcontribution', recordid: tenantcontributionid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((tenantContributions: TenantContribution) => {
      const newRecord = tenantContributions[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.tenantcontributionid),
        redofunc: () => copy(propertyid, tenantcontributionid, code)
      });
      tenantContributionsMap[newRecord.tenantcontributionid] = newRecord
      // merge results unique by tenantcontributionid
      const currentValue = tenantContributionsStream.getValue().value
      tenantContributionsStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'tenantcontributionid').sort((a, b) => alphanum(a.code, b.code))
      })
      tenantContributionsEventStream.next({ type: 'TENANTCONTRIBUTION_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      tenantContributionsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchTenantContributions(propertyid)
      return err
    })
}

function copies(propertyid: number, tenantcontributionid: number, count: number) {
  const currentValue = tenantContributionsStream.getValue().value;
  tenantContributionsStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'tenantcontribution', recordid: tenantcontributionid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((tenantcontributions: TenantContribution[]) => {
      push_undo({
        undofunc: () => _.forEach(tenantcontributions, newRecord => deleteOne(propertyid, newRecord.tenantcontributionid)),
        redofunc: () => copies(propertyid, tenantcontributionid, count)
      });
      _.forEach(tenantcontributions, newRecord => tenantContributionsMap[newRecord.tenantcontributionid] = newRecord)
      // merge results unique by tenantcontributionid
      const currentValue = tenantContributionsStream.getValue().value
      tenantContributionsStream.next({
        id,
        state: 'ok',
        value: _.unionBy(tenantcontributions, currentValue, 'tenantcontributionid').sort((a, b) => alphanum(a.code, b.code))
      })
      tenantContributionsEventStream.next({ type: 'TENANTCONTRIBUTION_COPIED', payload: tenantcontributions })
      return tenantcontributions
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      tenantContributionsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchTenantContributions(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  tenantcontributionid: number
): Promise<boolean | { message: string }> {
  const currentValue = tenantContributionsStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, tenantcontributionid)
  const refreshFn = () => fetchTenantContributions(propertyid)
  if (!(await handleReferences('tenantcontribution', tenantcontributionid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted tenantcontributionid
  const value = _(currentValue)
    .filter((p) => p.tenantcontributionid !== tenantcontributionid)
    .value()
  value.forEach((p) => (tenantContributionsMap[p.tenantcontributionid] = p))

  tenantContributionsStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(tenantcontributionid).then(() =>
  Client.del(`tenantcontribution?tenantcontributionid=eq.${tenantcontributionid}`)
    .then((tenantContributions: TenantContribution) => {
      delete tenantContributionsMap[tenantContributions.tenantcontributionid]
      push_undo({
        undofunc: () => {
          Client.post('tenantContributions', tenantContributions)
          tenantContributionsMap[tenantContributions.tenantcontributionid] = tenantContributions
          tenantContributionsStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, tenantcontributionid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      tenantContributionsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchTenantContributions(propertyid)
      return err
    })
  )
}

function patch(tenantContributions: TenantContribution): Promise<TenantContribution | { message: string }> {
  const currentValue = tenantContributionsStream.getValue().value
  const tenantcontributionid: number = tenantContributions.tenantcontributionid
  const currenttenantContributions = _.find(currentValue, { tenantcontributionid })
  const changeset = diff_obj(tenantContributions, currenttenantContributions)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currenttenantContributions),
    redofunc: () => patch(tenantContributions)
  })

  // optimistic update
  tenantContributionsStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {tenantcontributionid}), tenantContributions)
  })

  return _patches_.watch(tenantcontributionid, Client.patch(`tenantcontribution?tenantcontributionid=eq.${tenantcontributionid}`, changeset)
    .then((mf: TenantContribution[]) => {
      _.forEach(mf, (tenantContributions) => (tenantContributionsMap[tenantContributions.tenantcontributionid] = tenantContributions))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      tenantContributionsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currenttenantContributions, 'propertyid')
      if (propertyid) fetchTenantContributions(propertyid)
      return err
    }))
}

// public module interface //

const tenantContributionsService = {
  stream: () => tenantContributionsStream.asObservable(),
  value: () => tenantContributionsStream.getValue().value,
  eventStream: () => tenantContributionsEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  get,
  fetchTenantContributions,
  patch,
  refresh
}

export default tenantContributionsService